import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient from '../httpClient.ts';

// Types
export interface Notification {
    id: string;
    type: 'system' | 'user' | 'trading' | 'compliance' | 'marketing';
    priority: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    message: string;
    recipient: string;
    recipient_name?: string | null;
    recipient_email?: string | null;
    recipient_type: 'user' | 'customer' | 'lead' | 'group' | 'all';
    status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
    sent_at: string | null;
    read_at: string | null;
    created_by: string | null;
    metadata: Record<string, any> | null;
    created_at: string;
    updated_at: string;
    channels?: NotificationChannel[];
}

export interface NotificationChannel {
    id: string;
    notification_id: string;
    type: 'email' | 'sms' | 'push' | 'in_app';
    status: 'pending' | 'sent' | 'delivered' | 'failed';
    details: string | null;
    sent_at: string | null;
    delivered_at: string | null;
    retry_count: number;
    next_retry_at: string | null;
    metadata: Record<string, any> | null;
    created_at: string;
    updated_at: string;
}

// Enhanced Notification Template Types
export interface NotificationTemplate {
    id: string;
    name: string;
    slug: string;
    type: 'automatic' | 'manual' | 'custom';
    description: string | null;
    title: string;
    content: string;
    content_variations: Record<
        string,
        {
            subject: string | null;
            content: string;
            html_content: string | null;
            channel_specific_data: Record<string, any> | null;
        }
    > | null;
    channel_settings: Record<string, any> | null;
    channels: ('email' | 'sms' | 'push' | 'in_app')[];
    variables: string[];
    supported_languages: string[];
    default_language: string;
    is_active: boolean;
    is_system_template: boolean;
    usage_count: number;
    created_by: string | null;
    metadata: Record<string, any> | null;
    created_at: string;
    updated_at: string;
}

// Manual Notification Types
export interface ManualNotification {
    id: string;
    name: string;
    description: string | null;
    type: 'system' | 'user' | 'trading' | 'compliance' | 'marketing';
    priority: 'low' | 'medium' | 'high' | 'critical';
    subject: string;
    message: string;
    recipients: string[] | null;
    recipient_criteria: Record<string, any> | null;
    channels: ('email' | 'sms' | 'push' | 'in_app')[];
    status: 'draft' | 'scheduled' | 'sending' | 'completed' | 'cancelled';
    scheduled_at: string | null;
    sent_at: string | null;
    total_recipients: number;
    sent_count: number;
    failed_count: number;
    metadata: Record<string, any> | null;
    created_by: string;
    approved_by: string | null;
    approved_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface CreateNotificationData {
    type: 'system' | 'user' | 'trading' | 'compliance' | 'marketing';
    priority: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    message: string;
    recipient: string;
    recipient_type: 'user' | 'customer' | 'group' | 'all';
    channels: ('email' | 'sms' | 'push' | 'in_app')[];
    metadata?: Record<string, any>;
}

export interface CreateNotificationTemplateData {
    name: string;
    slug?: string;
    type: 'automatic' | 'manual' | 'custom';
    description?: string;
    channels: ('email' | 'sms' | 'push' | 'in_app')[];
    variables?: string[];
    supported_languages: string[];
    default_language: string;
    is_active?: boolean;
    is_system_template?: boolean;
    contents: Array<{
        language: string;
        channel: 'email' | 'sms' | 'push' | 'in_app';
        subject?: string;
        content: string;
        html_content?: string;
        channel_specific_data?: Record<string, any>;
    }>;
}

export interface CreateManualNotificationData {
    name: string;
    description?: string;
    type: 'system' | 'user' | 'trading' | 'compliance' | 'marketing';
    priority: 'low' | 'medium' | 'high' | 'critical';
    subject: string;
    message: string;
    recipients?: string[];
    recipient_criteria?: Record<string, any>;
    channels: ('email' | 'sms' | 'push' | 'in_app')[];
    scheduled_at?: string;
    metadata?: Record<string, any>;
}

export interface NotificationAnalytics {
    total_notifications: number;
    unread_notifications: number;
    read_rate: number;
    notifications_by_type: Record<string, number>;
    notifications_by_priority: Record<string, number>;
    notifications_by_status: Record<string, number>;
    channel_statistics: Array<{
        type: string;
        total: number;
        successful: number;
        success_rate: number;
    }>;
    period_hours: number;
}

export interface ManualNotificationAnalytics {
    total_notifications: number;
    draft_notifications: number;
    scheduled_notifications: number;
    completed_notifications: number;
    notifications_by_type: Record<string, number>;
    notifications_by_priority: Record<string, number>;
    success_rates: Array<{
        id: string;
        name: string;
        success_rate: number;
        failure_rate: number;
        total_recipients: number;
        sent_count: number;
        failed_count: number;
    }>;
    period_hours: number;
}

// ==================== REGULAR NOTIFICATIONS ====================

export const useNotifications = (
    filters: {
        type?: string;
        priority?: string;
        recipient?: string;
        status?: string;
        unread_only?: boolean;
        recent_hours?: number;
        sort_by?: string;
        sort_order?: 'asc' | 'desc';
        per_page?: number;
        page?: number;
    } = {}
) => {
    return useQuery<PaginatedResponse<Notification>>({
        queryKey: ['notifications', filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params.append(key, value.toString());
                }
            });

            const response = await httpClient.get(`/crm/notifications?${params}`);
            return response.data;
        },
    });
};

export const useNotification = (id: string) => {
    return useQuery({
        queryKey: ['notification', id],
        queryFn: async () => {
            const response = await httpClient.get(`/crm/notifications/${id}`);
            return response.data;
        },
        enabled: !!id,
    });
};

export const useCreateNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateNotificationData) => {
            const response = await httpClient.post('/crm/notifications', data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['notification-analytics'] });
            queryClient.invalidateQueries({ queryKey: ['unread-count'] });
        },
    });
};

export const useUpdateNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: Partial<CreateNotificationData> }) => {
            const response = await httpClient.put(`/crm/notifications/${id}`, data);
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['notification', id] });
            queryClient.invalidateQueries({ queryKey: ['notification-analytics'] });
        },
    });
};

export const useDeleteNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await httpClient.delete(`/crm/notifications/${id}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['notification-analytics'] });
        },
    });
};

export const useMarkNotificationAsRead = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await httpClient.post(`/crm/notifications/${id}/mark-read`);
            return response.data;
        },
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['notification', id] });
            queryClient.invalidateQueries({ queryKey: ['unread-count'] });
        },
    });
};

export const useResendNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await httpClient.post(`/crm/notifications/${id}/resend`);
            return response.data;
        },
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['notification', id] });
            queryClient.invalidateQueries({ queryKey: ['notification-analytics'] });
        },
    });
};

export const useMarkMultipleNotificationsAsRead = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (notificationIds: string[]) => {
            const response = await httpClient.post('/crm/notifications/mark-multiple-read', {
                notification_ids: notificationIds,
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['unread-count'] });
        },
    });
};

export const useUnreadNotificationCount = () => {
    return useQuery<number>({
        queryKey: ['unread-count'],
        queryFn: async () => {
            const response = await httpClient.get('/crm/notifications/unread-count');
            return response.data;
        },
        refetchInterval: 30000, // Refetch every 30 seconds
    });
};

export const useNotificationAnalytics = (hours: number = 24) => {
    return useQuery<NotificationAnalytics>({
        queryKey: ['notification-analytics', hours],
        queryFn: async () => {
            const response = await httpClient.get(
                `/crm/notifications/analytics/overview?hours=${hours}`
            );
            return response.data;
        },
    });
};

// ==================== NOTIFICATION TEMPLATES ====================

export const useNotificationTemplates = (
    filters: {
        type?: string;
        channel?: string;
        language?: string;
        active_only?: boolean;
        search?: string;
        sort_by?: string;
        sort_order?: 'asc' | 'desc';
        per_page?: number;
        page?: number;
    } = {}
) => {
    return useQuery<PaginatedResponse<NotificationTemplate>>({
        queryKey: ['notification-templates', filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params.append(key, value.toString());
                }
            });

            const response = await httpClient.get(`/crm/notification-templates?${params}`);
            return response.data;
        },
    });
};

export const useNotificationTemplate = (id: string) => {
    return useQuery({
        queryKey: ['notification-template', id],
        queryFn: async () => {
            const response = await httpClient.get(`/crm/notification-templates/${id}`);
            return response.data;
        },
        enabled: !!id,
    });
};

export const useCreateNotificationTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateNotificationTemplateData) => {
            const response = await httpClient.post('/crm/notification-templates', data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
        },
    });
};

export const useUpdateNotificationTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            id,
            data,
        }: {
            id: string;
            data: Partial<CreateNotificationTemplateData>;
        }) => {
            const response = await httpClient.put(`/crm/notification-templates/${id}`, data);
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
            queryClient.invalidateQueries({ queryKey: ['notification-template', id] });
        },
    });
};

export const useDeleteNotificationTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await httpClient.delete(`/crm/notification-templates/${id}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
        },
    });
};

export const useTestNotificationTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            id,
            data,
        }: {
            id: string;
            data: {
                data: Record<string, any>;
                language?: string;
                channel?: string;
                recipient_id: string;
                recipient_type: string;
            };
        }) => {
            const response = await httpClient.post(`/crm/notification-templates/${id}/test`, data);
            return response.data;
        },
    });
};

// ==================== QUICK EMAIL ====================

export const useSendQuickEmail = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: {
            recipient_id: string;
            recipient_type: 'customer' | 'lead';
            subject: string;
            content: string;
            template_id?: string;
            language?: string;
        }) => {
            const response = await httpClient.post('/crm/notifications/send-quick-email', data);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate notifications list to show the new sent email
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });
};

export const useSendCustomEmail = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: {
            recipient_id: string;
            recipient_type: 'customer' | 'lead';
            subject: string;
            customer_content: string;
            language?: string;
        }) => {
            const response = await httpClient.post('/crm/notifications/send-custom-email', data);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate notifications list to show the new sent email
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });
};

// ==================== MANUAL NOTIFICATIONS ====================

export const useManualNotifications = (
    filters: {
        status?: string;
        type?: string;
        priority?: string;
        created_by?: string;
        search?: string;
        sort_by?: string;
        sort_order?: 'asc' | 'desc';
        per_page?: number;
        page?: number;
    } = {}
) => {
    return useQuery<PaginatedResponse<ManualNotification>>({
        queryKey: ['manual-notifications', filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params.append(key, value.toString());
                }
            });

            const response = await httpClient.get(`/crm/manual-notifications?${params}`);
            return response.data;
        },
    });
};

export const useManualNotification = (id: string) => {
    return useQuery({
        queryKey: ['manual-notification', id],
        queryFn: async () => {
            const response = await httpClient.get(`/crm/manual-notifications/${id}`);
            return response.data;
        },
        enabled: !!id,
    });
};

export const useCreateManualNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateManualNotificationData) => {
            const response = await httpClient.post('/crm/manual-notifications', data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['manual-notifications'] });
            queryClient.invalidateQueries({ queryKey: ['manual-notification-analytics'] });
        },
    });
};

export const useUpdateManualNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            id,
            data,
        }: {
            id: string;
            data: Partial<CreateManualNotificationData>;
        }) => {
            const response = await httpClient.put(`/crm/manual-notifications/${id}`, data);
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['manual-notifications'] });
            queryClient.invalidateQueries({ queryKey: ['manual-notification', id] });
        },
    });
};

export const useDeleteManualNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await httpClient.delete(`/crm/manual-notifications/${id}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['manual-notifications'] });
        },
    });
};

export const useApproveManualNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await httpClient.post(`/crm/manual-notifications/${id}/approve`);
            return response.data;
        },
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ['manual-notifications'] });
            queryClient.invalidateQueries({ queryKey: ['manual-notification', id] });
        },
    });
};

export const useScheduleManualNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, scheduled_at }: { id: string; scheduled_at: string }) => {
            const response = await httpClient.post(`/crm/manual-notifications/${id}/schedule`, {
                scheduled_at,
            });
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['manual-notifications'] });
            queryClient.invalidateQueries({ queryKey: ['manual-notification', id] });
        },
    });
};

export const useSendManualNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await httpClient.post(`/crm/manual-notifications/${id}/send`);
            return response.data;
        },
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ['manual-notifications'] });
            queryClient.invalidateQueries({ queryKey: ['manual-notification', id] });
            queryClient.invalidateQueries({ queryKey: ['manual-notification-analytics'] });
        },
    });
};

export const useCancelManualNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await httpClient.post(`/crm/manual-notifications/${id}/cancel`);
            return response.data;
        },
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ['manual-notifications'] });
            queryClient.invalidateQueries({ queryKey: ['manual-notification', id] });
        },
    });
};

export const useManualNotificationAnalytics = (hours: number = 24) => {
    return useQuery<ManualNotificationAnalytics>({
        queryKey: ['manual-notification-analytics', hours],
        queryFn: async () => {
            const response = await httpClient.get(
                `/crm/manual-notifications/analytics/overview?hours=${hours}`
            );
            return response.data;
        },
    });
};

// ==================== UTILITY FUNCTIONS ====================

export const getPriorityColor = (priority: string): string => {
    switch (priority) {
        case 'critical':
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
        case 'high':
            return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
        case 'medium':
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
        case 'low':
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
};

export const getTypeColor = (type: string): string => {
    switch (type) {
        case 'system':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
        case 'trading':
            return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
        case 'compliance':
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
        case 'marketing':
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
        case 'user':
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
};

export const getStatusColor = (status: string): string => {
    switch (status) {
        case 'sent':
        case 'delivered':
        case 'completed':
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
        case 'pending':
        case 'scheduled':
        case 'draft':
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
        case 'failed':
        case 'cancelled':
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
        case 'read':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
};

export const formatNotificationAge = (createdAt: string): string => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffInHours = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
        return 'Just now';
    } else if (diffInHours < 24) {
        return `${diffInHours}h ago`;
    } else {
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays}d ago`;
    }
};

export const formatDateTime = (value: string | null | undefined): string => {
    if (!value) return '—';
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(new Date(value));
};

export const resolveRecipientName = (notification: {
    recipient_name?: string | null;
    metadata?: Record<string, any> | null;
    recipient: string;
}): string => {
    return (
        notification.recipient_name ||
        notification.metadata?.recipient_name ||
        notification.metadata?.name ||
        notification.recipient ||
        '—'
    );
};

/** CRM path for customer/lead recipient, or null if not linkable */
export const getRecipientCrmPath = (notification: {
    recipient: string;
    recipient_type?: string;
}): string | null => {
    const type = notification.recipient_type;
    const id = notification.recipient;
    if (!id) return null;
    if (type === 'customer') return `/customers/${id}`;
    if (type === 'lead') return `/leads/${id}`;
    return null;
};

export const getChannelIcon = (channel: string): string => {
    switch (channel) {
        case 'email':
            return '📧';
        case 'sms':
            return '📱';
        case 'push':
            return '🔔';
        case 'in_app':
            return '💬';
        default:
            return '📢';
    }
};
